"use client";

import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Copy, ChevronUp, ChevronDown, ChevronsUpDown, X, SlidersHorizontal, Plus } from "lucide-react";
import "@/pages/Dashboard/Clientes/Clientes.module.css";
import { useCustomers, CustomerFilters } from "@/pages/Dashboard/Clientes/hooks/useCustomers";
import { useUser } from "@/utils/userContext/UserContext";
import { Client } from "@/utils/models/Client";
import CustomerDetails from "@/pages/Dashboard/Clientes/components/CustomerDetails";
import CsvUploadArea from "@/pages/Dashboard/Clientes/components/CsvUploadArea";
import { useIsMobile } from "@/utils/common/mobileCheck";
import ClientRegisterModal from "@/pages/Dashboard/Clientes/components/ClientRegisterModal.tsx";
import DownloadExampleXlsx from "@/pages/Dashboard/Clientes/components/DownloadExampleXlsx";
import Pagination from "@/components/Pagination/Pagination";
import styles from "@/pages/Dashboard/Clientes/Clientes.module.css";
import { useInvalidateCache } from "@/utils/hooks/useInvalidateCache";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import api from "@/utils/connector/ApiConnector";

// Sort icon component
function SortIcon({ field, currentSort, currentOrder }: { field: string; currentSort: string; currentOrder: 'asc' | 'desc' }) {
  if (currentSort !== field) {
    return <ChevronsUpDown size={14} className={styles.sortIconInactive} />;
  }
  return currentOrder === 'asc' 
    ? <ChevronUp size={14} className={styles.sortIconActive} />
    : <ChevronDown size={14} className={styles.sortIconActive} />;
}

const Clientes: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // New filter states
  const [hasAffiliateFilter, setHasAffiliateFilter] = useState<'true' | 'false' | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(false);

  const userContext = useUser();
  const userData = userContext?.userData;
  const token = userData?.auth;

  // modals / detalhes
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsClient, setDetailsClient] = useState<any>(null);
  const [createClientModalOpen, setCreateClientModalOpen] = useState(false);
  const { invalidateClients } = useInvalidateCache();
  const modal = useGlobalModal();

  const isMobile = useIsMobile();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deletingClientId, setDeletingClientId] = useState<string | null>(null);

  const columns = [
    { key: "affiliateCode", label: "Código de Afiliado", renderMobile: false, sortable: false },
    { key: "postalBoxNumber", label: "Caixa Postal", renderMobile: false, sortable: true },
    { key: "name", label: "Empresa", renderMobile: true, sortable: true },
    { key: "legalRepresentativeName", label: "Representante Legal", renderMobile: false, sortable: true },
    { key: "legalRepresentativeDocument", label: "CPF", renderMobile: true, sortable: false },
    { key: "phone", label: "Telefone", renderMobile: false, sortable: true },
    { key: "createdAt", label: "Data de Entrada", renderMobile: false, sortable: true },
    { key: "affiliate", label: "Afiliado", renderMobile: false, sortable: false },
  ];

  const { fetchCustomers } = useCustomers();

  // Debounce search term
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, hasAffiliateFilter, dateFrom, dateTo, sortBy, sortOrder]);

  // Build filters object
  const filters: CustomerFilters = {
    query: debouncedSearch || undefined,
    hasAffiliate: hasAffiliateFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    sortBy,
    sortOrder,
  };

  // Handle column sort
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setHasAffiliateFilter('');
    setDateFrom('');
    setDateTo('');
    setSortBy('name');
    setSortOrder('asc');
  };

  const hasActiveFilters = searchTerm || hasAffiliateFilter || dateFrom || dateTo;

  // React Query for paginated clients
  const {
    data,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['clients', currentPage, pageSize, debouncedSearch, hasAffiliateFilter, dateFrom, dateTo, sortBy, sortOrder],
    queryFn: async () => {
      const parseItemsAndTotal = (rawData: any): { items: Client[]; total: number } => {
        let parsedItems: Client[] = [];
        if (Array.isArray(rawData)) parsedItems = rawData as Client[];
        else if (Array.isArray(rawData.items)) parsedItems = rawData.items as Client[];
        else if (Array.isArray(rawData.data)) parsedItems = rawData.data as Client[];
        else if (Array.isArray((rawData as any).clients)) parsedItems = (rawData as any).clients as Client[];

        const maybeTotal = rawData?.total ?? rawData?.count ?? rawData?.totalItems ?? rawData?.totalClients ?? null;
        const parsedTotal = Number.isFinite(Number(maybeTotal)) ? Number(maybeTotal) : parsedItems.length;
        return { items: parsedItems, total: parsedTotal };
      };

      const normalizedQuery = (debouncedSearch ?? "").trim().toLowerCase();

      // Busca local abrangente para cobrir também representante legal.
      if (normalizedQuery.length > 0) {
        const accumulated: Client[] = [];
        let pageNumber = 1;
        const pageSizeServer = 200;
        const MAX_ITEMS = 2000;
        let totalServer = 0;

        while (true) {
          const resp = await fetchCustomers(token!, pageNumber, pageSizeServer, {
            ...filters,
            query: undefined,
          });
          const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
          const { items: batch, total } = parseItemsAndTotal(raw);
          totalServer = total || totalServer;
          accumulated.push(...batch);

          if (accumulated.length >= totalServer) break;
          if (accumulated.length >= MAX_ITEMS) break;
          if (batch.length < pageSizeServer) break;
          pageNumber++;
        }

        const filtered = accumulated.filter((client) => {
          const fields = [
            client.name ?? "",
            client.legalRepresentativeName ?? "",
            client.email ?? "",
            client.companyDocument ?? "",
            client.legalRepresentativeDocument ?? "",
          ];
          return fields.some((field) => String(field).toLowerCase().includes(normalizedQuery));
        });

        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return { items: filtered.slice(start, end), total: filtered.length };
      }

      const resp = await fetchCustomers(token!, currentPage, pageSize, filters);
      const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
      return parseItemsAndTotal(raw);
    },
    enabled: !!token,
  });

  const clients = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Manual search button
  function listUsers() {
    refetch();
  }

  async function handleDeleteClient(client: Client) {
    if (!token || !client?.id) return;
    const displayName = client.name || client.legalRepresentativeName || client.email || client.id;
    const impactLines = [
      `Deseja realmente excluir o cliente "${displayName}"?`,
      "",
      "Impactos desta ação:",
      "- O cadastro e os dados do cliente serão removidos.",
      "- Reservas e assinaturas vinculadas a este cliente serão excluídas.",
      "- O cliente deixará de receber notificações automáticas e envios manuais.",
      "- O cliente deixará de aparecer em listas e segmentações (incluindo status de assinatura).",
      "- Indicadores do dashboard podem mudar (faturamento, churn, MRR, assinaturas do período).",
      "- Clientes afiliados a este registro ficarão sem vínculo de afiliado.",
      "",
      "Essa ação não pode ser desfeita.",
    ];
    const confirmed = window.confirm(impactLines.join("\n"));
    if (!confirmed) return;

    setDeletingClientId(client.id);
    try {
      await api.delete(`/customer/${client.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      modal?.show({ type: "success", title: "Sucesso", message: "Cliente excluído com sucesso.", autoClose: 3000 });
      await invalidateClients();
      await refetch();
    } catch (err: any) {
      const message = err?.response?.data?.message ?? err?.message ?? "Não foi possível excluir o cliente.";
      modal?.show({ type: "error", title: "Erro ao excluir", message, autoClose: 4000 });
    } finally {
      setDeletingClientId(null);
    }
  }

  async function copyId(id?: string) {
    if (!id) return;
    try {
      if (typeof window !== "undefined" && window.navigator.clipboard?.writeText) {
        await window.navigator.clipboard.writeText(id);
      } else {
        const ta = document.createElement("textarea");
        ta.value = id;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(prev => (prev === id ? null : prev)), 2000);
    } catch (err) {
      console.error("Erro ao copiar id:", err);
    }
  }

  return (
    <>
      <div className={styles.clientesHeader}>
        <div className={styles.headerTextGroup}>
          <h1 className={styles.gradientHeading}>Clientes</h1>
          <p className={`${styles.pageSubtitle} dashboard-page-subtitle`}>
            Gerencie sua base de clientes e dados de relacionamento.
          </p>
        </div>
        {!isMobile && (
          <div className={styles.displayInl}>
            <button className={styles.addButton} onClick={() => setCreateClientModalOpen(true)}>
              <Plus size={16} />
              Adicionar novo Cliente
            </button>
            <DownloadExampleXlsx />
            <CsvUploadArea />
          </div>
        )}
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.searchContainer}>
          <div className={styles.searchInputContainer}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar por empresa, representante legal, CNPJ ou email..."
              style={{ backgroundColor: "#F5F5F5" }}
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') listUsers(); }}
            />
          </div>
          <button 
            className={`${styles.filterToggle} ${showFilters ? styles.filterToggleActive : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal size={16} />
            Filtros {hasActiveFilters && <span className={styles.filterBadge}>●</span>}
          </button>
          {hasActiveFilters && (
            <button className={styles.clearFilters} onClick={clearFilters}>
              <X size={14} /> Limpar
            </button>
          )}
        </div>

        {showFilters && (
          <div className={styles.filtersRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Afiliado</label>
              <select 
                className={styles.filterSelect}
                value={hasAffiliateFilter}
                onChange={(e) => setHasAffiliateFilter(e.target.value as 'true' | 'false' | '')}
              >
                <option value="">Todos</option>
                <option value="true">Com afiliado</option>
                <option value="false">Sem afiliado</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Data de entrada (de)</label>
              <input
                type="date"
                className={styles.filterInput}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Data de entrada (até)</label>
              <input
                type="date"
                className={styles.filterInput}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
        )}

        {isMobile && (
          <button
            type="button"
            className={`${styles.addButton} ${styles.mobileNewClientButton}`}
            onClick={() => setCreateClientModalOpen(true)}
          >
            <Plus size={16} />
            Novo cliente
          </button>
        )}
      </div>

      <div className={styles.clientesTableCard}>
        <div className={styles.clientesTableContainer}>
          {loading ? (
            <div className={styles.loadingIndicator}>Carregando...</div>
          ) : (
            <table className={styles.clientesTable}>
              <thead>
                <tr>
                  {columns.filter(col => !isMobile || col.renderMobile).map(col => (
                    <th 
                      key={col.key}
                      className={col.sortable ? styles.sortableHeader : undefined}
                      onClick={col.sortable ? () => handleSort(col.key) : undefined}
                    >
                      <span className={styles.thContent}>
                        {col.label}
                        {col.sortable && (
                          <SortIcon field={col.key} currentSort={sortBy} currentOrder={sortOrder} />
                        )}
                      </span>
                    </th>
                  ))}
                  <th>Ação</th>
                </tr>
              </thead>

              <tbody id={styles.clientesTableBody}>
                {Array.isArray(clients) && clients.length > 0 ? (
                  clients.map((client) => (
                    <tr key={client.id}>
                      {columns.filter(col => !isMobile || col.renderMobile).map(col => (
                        <td key={col.key} data-label={col.label} className={col.key === "affiliateCode" ? styles.idCell : undefined}>
                          {(() => {
                            switch (col.key) {
                              case "legalRepresentativeDocument":
                                return client.legalRepresentativeDocument || "-";
                              case "name":
                                return client.name || "-";
                              case "postalBoxNumber":
                                return client.postalBoxNumber || "-";
                              case "legalRepresentativeName":
                                return client.legalRepresentativeName || "-";
                              case "phone":
                                return client.phone || "-";
                              case "createdAt":
                                return client.createdAt ? new Date(client.createdAt).toLocaleDateString() : "-";
                              case "affiliate":
                                return client.affiliate?.name || "-";
                              case "affiliateCode": {
                                const code = client.id ?? "";
                                return (
                                  <div className={styles.idContainer}>
                                    <button
                                      type="button"
                                      className={styles.copyBtn}
                                      aria-label={`Copiar código ${code}`}
                                      onClick={() => copyId(code)}
                                      title="Copiar código"
                                    >
                                      <Copy size={14} />
                                    </button>
                                    {copiedId === code && <span className={styles.copiedBadge}>Copiado</span>}
                                  </div>
                                );
                              }
                              default:
                                return (client as any)[col.key] ?? "-";
                            }
                          })()}
                        </td>
                      ))}

                      <td>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <button
                            className={styles.editButton}
                            type="button"
                            onClick={() => {
                              setDetailsModalOpen(true);
                              setDetailsClient(client);
                            }}
                          >
                            +Detalhes
                          </button>
                          <button
                            className={styles.editButton}
                            type="button"
                            disabled={deletingClientId === client.id}
                            onClick={() => handleDeleteClient(client)}
                            style={{ background: "#b91c1c", color: "#fff" }}
                          >
                            {deletingClientId === client.id ? "Excluindo..." : "Excluir"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length + 1} className={styles.textCenter}>
                      Nenhum cliente encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

          )}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={total}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Modal de Detalhes */}
      {detailsModalOpen && detailsClient && (
        <CustomerDetails
          customer={detailsClient}
          onClose={() => setDetailsModalOpen(false)}
          isOpen={detailsModalOpen}
          onSaved={(updated) => {
            setDetailsClient(updated);
            setDetailsModalOpen(false);
          }}
        />
      )}

      <ClientRegisterModal
        isOpen={createClientModalOpen}
        onClose={() => setCreateClientModalOpen(false)}
        onSuccess={async () => {
          await invalidateClients();
          await refetch();
          setCreateClientModalOpen(false);
        }}
      />
    </>
  );
};

export default Clientes;
